import { Link } from '@tanstack/react-router';
import {
  PageSection,
  cmsPageImageUrl,
  cmsPlainTextExcerpt,
  pageSectionLabel,
  pageSectionTranslateKey,
  unescapeCmsText,
  type CmsPage,
} from '@/domain';
import { cn } from '@/lib/cn';
import { buttonVariants } from '@/ui/common';

const CMS_PROSE_CLASS =
  'prose-cms text-body-m text-text-secondary [&_a]:text-text-primary [&_a]:underline [&_h1]:mb-2 [&_h1]:text-heading-s [&_h1]:text-text-primary [&_h2]:mb-2 [&_h2]:text-heading-s [&_h2]:text-text-primary [&_h3]:mb-2 [&_h3]:text-heading-s [&_h3]:text-text-primary [&_li]:mb-1 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5';

export interface CmsPagePanelProps {
  page: CmsPage;
  usersBaseUrl: string;
  accountGuid: string;
  label: (key: string, fallback: string) => string;
  className?: string;
}

export function CmsPagePanel({
  page,
  usersBaseUrl,
  accountGuid,
  label,
  className,
}: CmsPagePanelProps) {
  const sectionKey = pageSectionTranslateKey(page.Position);
  const sectionFallback = pageSectionLabel(page.Position);
  const sectionTitle =
    sectionKey && sectionFallback
      ? label(sectionKey, sectionFallback)
      : sectionFallback;

  const title =
    page.Title?.trim() || page.Url?.trim() || label('ContentPage', 'Content page');
  const lead = cmsPlainTextExcerpt(page.Text, 160);
  const htmlBody = unescapeCmsText(page.Text);
  const imageUrl =
    page.Image?.trim() && accountGuid
      ? cmsPageImageUrl(usersBaseUrl, accountGuid, page.Id, page.Image)
      : null;
  const showRgTools = page.Position === PageSection.ResponsibleGambling;

  return (
    <div className={cn('mx-auto flex w-full max-w-[960px] flex-col gap-6', className)}>
      {sectionTitle ? <CmsMeta sectionLabel={sectionTitle} /> : null}

      <CmsHero eyebrow={sectionTitle} title={title} lead={lead} />

      {imageUrl || htmlBody ? (
        <CmsRichText imageUrl={imageUrl} htmlBody={htmlBody} />
      ) : null}

      {showRgTools ? (
        <CmsLinkList
          title={label('AccountTools', 'Account tools')}
          links={[
            {
              href: '/profile/deposit-limits',
              label: label('DepositLimits', 'Deposit limits'),
            },
            {
              href: '/profile/self-exclusion',
              label: label('SelfExclusion', 'Self-exclusion'),
            },
          ]}
        />
      ) : null}

      <CmsCta
        heading={label('NeedHelp', 'Need help?')}
        body={label(
          'CmsContactSupportHint',
          'Contact support or visit gambling therapy resources linked in the footer.',
        )}
        actionLabel={label('ContactSupport', 'Contact support')}
      />
    </div>
  );
}

export interface CmsMetaProps {
  sectionLabel: string;
  className?: string;
}

export function CmsMeta({ sectionLabel, className }: CmsMetaProps) {
  return (
    <p className={cn('text-caption tracking-[0.15px] text-text-secondary', className)}>
      CMS · {sectionLabel}
    </p>
  );
}

export interface CmsHeroProps {
  eyebrow?: string;
  title: string;
  lead?: string;
  className?: string;
}

export function CmsHero({ eyebrow, title, lead, className }: CmsHeroProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-md bg-bg-elevated px-7 py-8',
        className,
      )}
    >
      {eyebrow ? (
        <p className="text-label-s tracking-[0.2px] text-text-secondary">{eyebrow}</p>
      ) : null}
      <h1 className="text-display-m text-text-primary">{title}</h1>
      {lead ? <p className="text-body-l text-text-secondary">{lead}</p> : null}
    </div>
  );
}

export interface CmsRichTextProps {
  htmlBody?: string;
  imageUrl?: string | null;
  className?: string;
}

export function CmsRichText({ htmlBody, imageUrl, className }: CmsRichTextProps) {
  if (!htmlBody && !imageUrl) return null;

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-md bg-bg-surface px-7 py-5',
        className,
      )}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="w-full rounded-md object-cover"
          draggable={false}
        />
      ) : null}
      {htmlBody ? (
        <div
          className={CMS_PROSE_CLASS}
          dangerouslySetInnerHTML={{ __html: htmlBody }}
        />
      ) : null}
    </div>
  );
}

export interface CmsLinkListItem {
  href: string;
  label: string;
}

export interface CmsLinkListProps {
  title: string;
  links: readonly CmsLinkListItem[];
  className?: string;
}

export function CmsLinkList({ title, links, className }: CmsLinkListProps) {
  if (links.length === 0) return null;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-md bg-bg-surface px-7 py-5',
        className,
      )}
    >
      <h2 className="text-heading-s text-text-primary">{title}</h2>
      <ul className="flex flex-col gap-2">
        {links.map((link) => (
          <li key={link.href} className="flex gap-2 text-body-m text-text-primary">
            <span aria-hidden>•</span>
            <Link to={link.href} className="hover:text-text-secondary">
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface CmsCtaProps {
  heading: string;
  body: string;
  actionLabel: string;
  className?: string;
}

export function CmsCta({ heading, body, actionLabel, className }: CmsCtaProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-start gap-3 rounded-md bg-bg-elevated px-7 py-6',
        className,
      )}
    >
      <h2 className="text-heading-s text-text-primary">{heading}</h2>
      <p className="text-body-m text-text-secondary">{body}</p>
      <Link
        to="/page/$slug"
        params={{ slug: 'support' }}
        className={cn(buttonVariants({ variant: 'primary', size: 'lg' }), 'w-fit')}
      >
        {actionLabel}
      </Link>
    </div>
  );
}
